import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import {
  type UnitSystem,
  getStoredUnitSystem,
  storeUnitSystem,
  formatDistance,
  formatDepth,
  formatDepthRange,
  formatTemp,
  formatTempShort,
  formatWindSpeed,
  unitLabels,
} from '@/lib/units';

interface UnitsContextValue {
  system: UnitSystem;
  setSystem: (s: UnitSystem) => void;
  fmtDistance: (km: number) => string;
  fmtDepth: (meters: number) => string;
  fmtDepthRange: (range: string) => string;
  fmtTemp: (celsius: number) => string;
  fmtTempShort: (celsius: number) => string;
  fmtWind: (kmh: number) => string;
  labels: ReturnType<typeof unitLabels>;
}

const UnitsContext = createContext<UnitsContextValue | null>(null);

export const UnitsProvider = ({ children }: { children: ReactNode }) => {
  const [system, setSystemState] = useState<UnitSystem>(getStoredUnitSystem);

  const setSystem = useCallback((s: UnitSystem) => {
    setSystemState(s);
    storeUnitSystem(s);
  }, []);

  const value: UnitsContextValue = {
    system,
    setSystem,
    fmtDistance: (km) => formatDistance(km, system),
    fmtDepth: (m) => formatDepth(m, system),
    fmtDepthRange: (r) => formatDepthRange(r, system),
    fmtTemp: (c) => formatTemp(c, system),
    fmtTempShort: (c) => formatTempShort(c, system),
    fmtWind: (kmh) => formatWindSpeed(kmh, system),
    labels: unitLabels(system),
  };

  return <UnitsContext.Provider value={value}>{children}</UnitsContext.Provider>;
};

export const useUnits = (): UnitsContextValue => {
  const ctx = useContext(UnitsContext);
  if (!ctx) throw new Error('useUnits must be used within UnitsProvider');
  return ctx;
};
